/*!
 * controlXY.js -- Multitouch XY Control Pad
 * Part of oscillaScore control plane
 * (c) 2025 Rob Canning -- GPLv3
 *
 * controlXY(...) defines a persistent, multitouch control surface.
 * Multiple handles can be constrained to a bounding shape and each
 * continuously publishes normalized X/Y values (0..1, up = 1).
 *
 * Features:
 *   - Sequence transport controls in launcher bar ([>]/[X])
 *   - Glow/pulsate effect when handles are touched
 *   - Mute/manual override: click handle to show toggle overlay
 */

import { publish } from "../control/paramBinding.js";
import "./osc.js";
import { sendOSC } from "../system/oscillaOSCClient.js";
import { mapToSpatial, buildOSCArgs, formatSpatialOverlay } from "../control/spatialMap.js";
import * as shared from "../control/controlShared.js";
import { truthy, isOff } from "../parser/cueUtils.js";
import {
  parseHmode, parseRotRange, constrainRotation,
  normalizeRotation, denormalizeRotation
} from "../control/rotationMath.js";
import { onReset } from "../system/teardown.js";

// ============================================================================
// Global muted handles set - handles in this set ignore automation updates
// ============================================================================
const mutedHandles = new Set();

/** Is a handle muted (in manual override mode)? */
function isHandleMuted(uid, handleId) {
  return mutedHandles.has(`${uid}:${handleId}`);
}

/** Toggle a handle's mute; returns the new state. */
function toggleHandleMuted(uid, handleId) {
  const key = `${uid}:${handleId}`;
  const muted = !mutedHandles.has(key);
  if (muted) mutedHandles.add(key); else mutedHandles.delete(key);
  return muted;
}

// Rotation math functions (parseHmode, normalizeHmodeValue, parseRotRange,
// constrainRotation, normalizeRotation, denormalizeRotation) come from
// ../control/rotationMath.js

/**
 * handleControlXYCue()
 */
export function handleControlXYCue(el, args = [], options = {}) {
  // Parse args
  const cfg = {};
  for (const a of args) {
    if (!a?.type) continue;
    cfg[a.type] = a.value;
  }

  const uid = cfg.uid || el.id;
  if (!uid) {
    console.warn("[controlXY] Missing uid");
    return;
  }

  // Registered again (a rescan, a page revisit, an editor save): the old pad
  // goes first, with its listeners, launcher, labels and handle clones.
  window._controlXYPads?.[uid]?.cleanup?.();
  const ac = new AbortController();
  const signal = ac.signal;

  // Resolve bounds element
  const svg = el.ownerSVGElement;
  if (!svg) {
    console.warn("[controlXY] No parent SVG found");
    return;
  }

  let boundsEl;
  const boundsId = cfg.bounds;
  
  if (!boundsId || boundsId === "self") {
    boundsEl = el;
  } else {
    boundsEl = svg.getElementById(boundsId);
    if (!boundsEl) {
      console.warn("[controlXY] bounds element not found:", boundsId);
      return;
    }
  }

  // Parse touchpoints
  let touchptIds = cfg.touchpt || cfg.handle; // support old 'handle' for backwards compat
  if (!touchptIds) {
    console.warn("[controlXY] Missing touchpt parameter");
    return;
  }
  if (!Array.isArray(touchptIds)) {
    touchptIds = [touchptIds];
  }

  // Parse rotation handles (optional)
  // Only treat cfg.handle as rotation handles when cfg.touchpt was explicitly
  // provided. Otherwise cfg.handle is being used as the touchpoint parameter.
  let rotHandleIds = cfg.touchpt ? (cfg.handle || null) : null;
  let rotHandles = [];
  let sharedHandleMode = false;
  let sharedHandleEl = null;
  
  if (rotHandleIds) {
    if (!Array.isArray(rotHandleIds)) {
      // Single handle shared across all touchpoints
      sharedHandleMode = true;
      sharedHandleEl = svg.getElementById(rotHandleIds);
      if (!sharedHandleEl) {
        console.warn(`[controlXY] shared rotation handle not found: ${rotHandleIds}`);
        sharedHandleMode = false;
      } else {
        // Create array of nulls - we'll handle the shared element separately
        rotHandles = touchptIds.map(() => null);
      }
    } else {
      // Parallel array of handles
      rotHandles = rotHandleIds;
    }
  }

  // Parse hmode - rotation mode for each touchpoint (only if we have rotation handles)
  const hmodes = rotHandleIds ? parseHmode(cfg.hmode, touchptIds.length) : Array(touchptIds.length).fill(null);
  const rotRangeGlobal = cfg.rotRange; // Store global rotRange config

  const touchptEls = touchptIds.map((id, index) => {
    const touchptEl = svg.getElementById(id);
    if (!touchptEl) {
      console.warn(`[controlXY] touchpt element not found: ${id}`);
      return null;
    }
    
    // Get rotation handle for this touchpoint (if any)
    let rotHandleEl = null;
    const rotHandleId = rotHandles[index];
    
    // In shared handle mode, individual touchpoints don't get the handle
    // (it will be managed globally)
    if (!sharedHandleMode && rotHandleId) {
      rotHandleEl = svg.getElementById(rotHandleId);
      if (!rotHandleEl) {
        console.warn(`[controlXY] rotation handle element not found: ${rotHandleId}`);
      }
    }
    
    return { 
      id, 
      el: touchptEl, 
      rotHandleId, 
      rotHandleEl,
      hmode: rotHandleIds ? (hmodes[index] || 'continuous') : null,  // Ensure default is continuous
      rotRange: rotHandleIds ? parseRotRange(rotRangeGlobal, hmodes[index] || 'continuous') : null // Calculate if we have rotation
    };
  }).filter(Boolean);

  if (touchptEls.length === 0) {
    console.warn("[controlXY] No valid touchpoints found");
    return;
  }

  // Parse options
  // label:1 / true shows labels; osc:0 / false and launcher:0 / false turn
  // those off, 1 / true is on. A number above 1 is osc:'s throttle (ms) and
  // launcher:'s slot count.
  const showLabels = truthy(cfg.label);
  const oscEnabled = !isOff(cfg.osc);
  const oscThrottle = typeof cfg.osc === "number" && cfg.osc > 1 ? cfg.osc : 30;
  // spatial:plan|dome -- each handle is a sound source on the circle that
  // fits the bounds (see spatialMap.js): sent as format: (aed by default) to
  // <oscAddr>/<handle id>, or /oscilla/spat/<handle id> without an oscAddr
  const spatialMode = (() => {
    const m = String(cfg.spatial ?? "").toLowerCase();
    return m === "dome" ? "dome" : (m === "plan" || m === "1" || m === "true") ? "plan" : null;
  })();
  const spatialFormat = String(cfg.format || "aed").toLowerCase();
  // plan: elevation from a rotation handle's p when there is one, else elev: (degrees)
  const spatialElev = Number(cfg.elev) || 0;
  const oscAddr = cfg.oscAddr || (spatialMode ? "spat" : `controlXY/${uid}`);
  const launcherEnabled = !isOff(cfg.launcher);
  const launcherSlots = typeof cfg.launcher === "number" && cfg.launcher > 1 ? cfg.launcher : 8;
  const launcherBanks = typeof cfg.banks === "number" ? cfg.banks : 3;

  // Use getBoundingClientRect for all coordinate conversions.
  // getCTM() maps element-local → SVG viewport units (not screen pixels), so
  // composing it with getScreenCTM().inverse() (which expects screen pixels as
  // input) gives wrong results.  getBoundingClientRect() accounts for ALL CSS
  // transforms (including the #scrollStage translateX scroll), and dividing by
  // the viewBox:renderedSize ratio gives correct SVG-unit coordinates that are
  // self-consistent across svgPointFromEvent, bbox, and originalCenter. The
  // bbox and centres are measured once, here, in viewBox units, which no zoom
  // changes; svgPointFromEvent takes the ratio as it is at each event.
  const svgRenderedRect = svg.getBoundingClientRect();
  const vbScaleX = svg.viewBox.baseVal.width / svgRenderedRect.width;
  const vbScaleY = svg.viewBox.baseVal.height / svgRenderedRect.height;
  const bbox = (() => {
    const r = boundsEl.getBoundingClientRect();
    return {
      x: (r.left - svgRenderedRect.left) * vbScaleX,
      y: (r.top  - svgRenderedRect.top)  * vbScaleY,
      width:  r.width  * vbScaleX,
      height: r.height * vbScaleY
    };
  })();
  const parent = boundsEl.parentNode;
  // the circle a spatial pad maps onto: the largest that fits the bounds
  const circle = {
    cx: bbox.x + bbox.width / 2,
    cy: bbox.y + bbox.height / 2,
    radius: Math.min(bbox.width, bbox.height) / 2
  };

  let launcherElement = null;
  if (launcherEnabled) {
    launcherElement = createLauncher(uid, bbox, launcherSlots, launcherBanks, parent, signal);
  }

  // Helper functions
  function svgPointFromEvent(e) {
    // Re-read each call: the scroll moves the rect, and a zoom, a window
    // resize or a page refit change its size, and so the viewBox ratio.
    const r = svg.getBoundingClientRect();
    return {
      x: (e.clientX - r.left) * (svg.viewBox.baseVal.width / r.width),
      y: (e.clientY - r.top)  * (svg.viewBox.baseVal.height / r.height)
    };
  }

  function clamp(val, min, max) {
    return Math.max(min, Math.min(max, val));
  }
  
  // Apply rotation transform to both touchpoint and rotation handle
  // Handle is positioned on the perimeter of the touchpoint
  function updateRotationTransform(touchptEl, rotHandleEl, touchptX, touchptY, 
                                   touchptOffsetX, touchptOffsetY,
                                   touchptRadius, handleOriginalCX, handleOriginalCY, 
                                   touchptOriginalCX, touchptOriginalCY, angleDeg) {
    if (!rotHandleEl) return;
    
    // Our zero is 7 o'clock (120° in standard SVG coords)
    const standardAngle = angleDeg + 120;
    const angleRad = (standardAngle * Math.PI) / 180;
    
    // Rotate touchpoint around its ORIGINAL center, then translate
    // SVG transforms read right-to-left, so this rotates first, then translates
    touchptEl.setAttribute("transform", 
      `translate(${touchptOffsetX}, ${touchptOffsetY}) rotate(${standardAngle} ${touchptOriginalCX} ${touchptOriginalCY})`);
    
    // Calculate handle position on the perimeter (relative to current touchpoint position)
    const handleX = touchptX + Math.cos(angleRad) * touchptRadius;
    const handleY = touchptY + Math.sin(angleRad) * touchptRadius;
    
    // Translate handle from its original position to perimeter position
    const handleOffsetX = handleX - handleOriginalCX;
    const handleOffsetY = handleY - handleOriginalCY;
    
    // Rotate handle around its ORIGINAL center, then translate
    rotHandleEl.setAttribute("transform", 
      `translate(${handleOffsetX}, ${handleOffsetY}) rotate(${standardAngle} ${handleOriginalCX} ${handleOriginalCY})`);
  }

  function createLabel(handleEl) {
    const label = document.createElementNS("http://www.w3.org/2000/svg", "text");
    label.setAttribute("class", "controlxy-label");
    label.setAttribute("text-anchor", "middle");
    label.setAttribute("dominant-baseline", "auto");
    label.setAttribute("font-family", "monospace");
    label.setAttribute("font-size", "12");
    label.setAttribute("fill", "#000");
    label.setAttribute("pointer-events", "none");
    label.textContent = "0.50, 0.50";
    handleEl.parentNode.insertBefore(label, handleEl.nextSibling);
    return label;
  }

  function updateLabel(label, handleEl, normX, normY, offsetX, offsetY) {
    if (!label) return;
    const hbox = handleEl.getBBox();
    const labelX = hbox.x + hbox.width / 2 + offsetX;
    const labelY = hbox.y + offsetY - 8;
    label.setAttribute("x", labelX);
    label.setAttribute("y", labelY);
    label.textContent = `${normX.toFixed(2)}, ${normY.toFixed(2)}`;
  }

  let lastTouchedHandle = null;

  // Setup shared handle if in shared mode
  if (sharedHandleMode && sharedHandleEl) {
    // Get shared handle's original properties for cloning
    sharedHandleEl.removeAttribute("transform");
    const shbbox = sharedHandleEl.getBBox();
    const sharedHandleOriginalCX = shbbox.x + shbbox.width / 2;
    const sharedHandleOriginalCY = shbbox.y + shbbox.height / 2;
    
    // Store shared handle template info
    window._controlXYSharedHandle = window._controlXYSharedHandle || {};
    window._controlXYSharedHandle[uid] = {
      templateEl: sharedHandleEl,
      originalCX: sharedHandleOriginalCX,
      originalCY: sharedHandleOriginalCY,
      clones: []
    };
    
    // Hide the original template element
    sharedHandleEl.style.display = 'none';
  }

  // Per-touchpoint state
  const handles = touchptEls.map(({ id, el: touchptEl, rotHandleId, rotHandleEl }, index) => {
    const originalTransform = touchptEl.getAttribute("transform") || "";
    touchptEl.removeAttribute("transform");

    // getBBox() still needed for touchptRadius (element-local size, no transforms needed).
    const hbbox = touchptEl.getBBox();
    // Natural center in SVG-root units: use getBoundingClientRect (accounts for
    // all CSS transforms including #scrollStage scroll) relative to the SVG root.
    // vbScaleX/Y are closed over from above and are invariant to scroll.
    const hRect = touchptEl.getBoundingClientRect();
    const svgNow = svg.getBoundingClientRect();
    let originalCenterX = (hRect.left + hRect.width  / 2 - svgNow.left) * vbScaleX;
    let originalCenterY = (hRect.top  + hRect.height / 2 - svgNow.top)  * vbScaleY;

    if (originalTransform) {
      touchptEl.setAttribute("transform", originalTransform);
    }

    let curX = bbox.x + bbox.width / 2;
    let curY = bbox.y + bbox.height / 2;
    if (spatialMode) {
      // a source starts where the score puts it (inside the circle), not
      // at the centre -- several would start on top of each other
      curX = originalCenterX; curY = originalCenterY;
      const dx = curX - circle.cx, dy = curY - circle.cy, d = Math.hypot(dx, dy);
      if (d > circle.radius) { curX = circle.cx + dx * circle.radius / d; curY = circle.cy + dy * circle.radius / d; }
    }
    const label = showLabels ? createLabel(touchptEl) : null;
    let offsetX = curX - originalCenterX;
    let offsetY = curY - originalCenterY;

    touchptEl.setAttribute("transform", `translate(${offsetX}, ${offsetY})`);
    touchptEl.style.pointerEvents = "auto";
    touchptEl.style.cursor = "grab";
    touchptEl.style.touchAction = "none";
    touchptEl.classList.add("controlxy-handle");
    touchptEl.dataset.controlxyUid = uid;
    touchptEl.dataset.controlxyHandle = id;
    touchptEl.parentNode.appendChild(touchptEl);

    if (label) {
      updateLabel(label, touchptEl, 0.5, 0.5, offsetX, offsetY);
    }

    // Rotation handle setup (if present)
    let rotHandle = null;
    let curAngle = 0; // in degrees, 0 = 7 o'clock (120° standard SVG)
    
    // Calculate touchpoint radius for handle positioning on perimeter
    const touchptRadius = Math.max(hbbox.width, hbbox.height) / 2;
    
    if (rotHandleEl) {
      // Individual handle for this touchpoint
      rotHandleEl.style.pointerEvents = "auto";
      rotHandleEl.style.cursor = "grab";
      rotHandleEl.style.touchAction = "none";
      rotHandleEl.classList.add("controlxy-rotation-handle");
      rotHandleEl.dataset.controlxyUid = uid;
      rotHandleEl.dataset.controlxyTouchpt = id;
      rotHandleEl.parentNode.appendChild(rotHandleEl);
      
      // Get rotation handle's original center
      rotHandleEl.removeAttribute("transform");
      const rhbbox = rotHandleEl.getBBox();
      const rotHandleOriginalCX = rhbbox.x + rhbbox.width / 2;
      const rotHandleOriginalCY = rhbbox.y + rhbbox.height / 2;
      
      rotHandle = {
        id: rotHandleId,
        el: rotHandleEl,
        originalCX: rotHandleOriginalCX,
        originalCY: rotHandleOriginalCY,
        dragging: false,
        pointerId: null
      };
      
      // Apply initial position
      updateRotationTransform(touchptEl, rotHandleEl, curX, curY, offsetX, offsetY, 
                              touchptRadius, rotHandleOriginalCX, rotHandleOriginalCY, 
                              originalCenterX, originalCenterY, curAngle);
    } else if (sharedHandleMode) {
      // Using shared handle - create a clone for this touchpoint
      const shared = window._controlXYSharedHandle[uid];
      const clonedHandle = shared.templateEl.cloneNode(true);
      
      // Setup cloned handle
      clonedHandle.removeAttribute('id'); // Don't duplicate IDs
      clonedHandle.style.display = '';
      clonedHandle.style.pointerEvents = "auto";
      clonedHandle.style.cursor = "grab";
      clonedHandle.style.touchAction = "none";
      clonedHandle.classList.add("controlxy-rotation-handle", "controlxy-shared-handle-clone");
      clonedHandle.dataset.controlxyUid = uid;
      clonedHandle.dataset.controlxyTouchpt = id;
      
      // Insert clone into SVG
      shared.templateEl.parentNode.appendChild(clonedHandle);
      
      // Get clone's center (should match template)
      const cloneBbox = clonedHandle.getBBox();
      const cloneCX = cloneBbox.x + cloneBbox.width / 2;
      const cloneCY = cloneBbox.y + cloneBbox.height / 2;
      
      rotHandle = {
        shared: true,
        id: 'shared_clone',
        el: clonedHandle,
        originalCX: cloneCX,
        originalCY: cloneCY,
        dragging: false,
        pointerId: null
      };
      
      // Store reference to clone
      shared.clones.push({ handle: rotHandle, touchptId: id });
      
      // Apply initial position
      updateRotationTransform(touchptEl, clonedHandle, curX, curY, offsetX, offsetY, 
                              touchptRadius, cloneCX, cloneCY, 
                              originalCenterX, originalCenterY, curAngle);
    }

    return {
      id,
      el: touchptEl,
      label,
      originalCenterX,
      originalCenterY,
      curX,
      curY,
      offsetX,
      offsetY,
      pointerId: null,
      dragging: false,
      lastOscSent: 0,
      muteOverlay: null,
      muteOverlayTimer: null,
      showOverlayTimer: null,
      // Rotation properties
      rotHandle,
      curAngle,
      touchptRadius,
      // Rotation mode and range
      hmode: touchptEls[index].hmode,
      rotRange: touchptEls[index].rotRange
    };
  });

  // Emit values
  // -- Sync throttle for broadcasting position to other clients --
  let _lastSyncSent = 0;
  const _syncThrottle = 50; // ms between sync messages
  let _isRemoteUpdate = false;

  function emit(handle) {
    const normX = clamp((handle.curX - bbox.x) / bbox.width, 0, 1);
    const normY = clamp(1 - (handle.curY - bbox.y) / bbox.height, 0, 1);
    
    // Calculate normalized rotation (0-1) based on hmode
    let normP = 0;
    if (handle.rotHandle) {
      if (handle.hmode === 'limited' && handle.rotRange) {
        // For limited mode, map the range to 0-1
        normP = handle.curAngle / handle.rotRange;
        normP = clamp(normP, 0, 1);
      } else {
        // For continuous mode, full 360° maps to 0-1
        normP = ((handle.curAngle % 360) + 360) % 360 / 360;
      }
    }

    const publishData = {
      handle: handle.id,
      x: normX,
      y: normY,
      [`${handle.id}.x`]: normX,
      [`${handle.id}.y`]: normY
    };
    
    // Add rotation if handle has rotation capability
    if (handle.rotHandle) {
      publishData.p = normP;
      publishData[`${handle.id}.p`] = normP;
    }

    let sp = null;
    if (spatialMode) {
      const elev01 = handle.rotHandle && spatialMode === "plan"
        ? normP
        : clamp(spatialElev / 180 + 0.5, 0, 1);
      sp = mapToSpatial(spatialMode, handle.curX - circle.cx, handle.curY - circle.cy, circle.radius, elev01);
      publishData[`${handle.id}.azi`] = sp.aziNorm;
      publishData[`${handle.id}.elev`] = sp.elevNorm;
      publishData[`${handle.id}.dist`] = sp.distNorm;
    }

    publish("controlXY", uid, publishData);

    if (handle.label && sp) {
      handle.label.textContent = formatSpatialOverlay(sp, spatialFormat);
      const hbox = handle.el.getBBox();
      handle.label.setAttribute("x", hbox.x + hbox.width / 2 + handle.offsetX);
      handle.label.setAttribute("y", hbox.y + handle.offsetY - 8);
    } else if (handle.label) {
      const labelText = handle.rotHandle 
        ? `${normX.toFixed(2)}, ${normY.toFixed(2)}, ${normP.toFixed(2)}`
        : `${normX.toFixed(2)}, ${normY.toFixed(2)}`;
      handle.label.textContent = labelText;
      const hbox = handle.el.getBBox();
      const labelX = hbox.x + hbox.width / 2 + handle.offsetX;
      const labelY = hbox.y + handle.offsetY - 8;
      handle.label.setAttribute("x", labelX);
      handle.label.setAttribute("y", labelY);
    }

    if (oscEnabled) {
      const now = performance.now();
      if (now - handle.lastOscSent >= oscThrottle) {
        handle.lastOscSent = now;
        const addr = (handles.length > 1 || (spatialMode && !cfg.oscAddr)) ? `${oscAddr}/${handle.id}` : oscAddr;
        
        // Include rotation in OSC if handle has rotation capability
        const args = sp ? buildOSCArgs(sp, spatialFormat)
          : handle.rotHandle ? [normX, normY, normP] : [normX, normY];
        
        sendOSC({
          type: "osc_value",
          addr: addr,
          args: args,
          float: !!sp,
          timestamp: Date.now()
        });
      }
    }

    // Broadcast position to other clients (throttled, skip if this is a remote update
    // or if a tween is running — tweens sync via preset_recall command, not position stream)
    // In composition/ensemble mode, manual moves stay local (only preset recalls sync)
    const presetScope = window.presetScope || 'user';
    if (!_isRemoteUpdate && !window._xyActiveTweens?.has(uid) && presetScope !== 'composition') {
      const now = performance.now();
      if (now - _lastSyncSent >= _syncThrottle) {
        _lastSyncSent = now;
        const sock = window.socket;
        if (sock && sock.readyState === WebSocket.OPEN) {
          sock.send(JSON.stringify({
            type: "pos_sync", kind: "xy",
            uid, handleId: handle.id,
            x: normX, y: normY
          }));
        }
      }
    }
  }

  function applyPosition(handle, targetX, targetY) {
    handle.curX = clamp(targetX, bbox.x, bbox.x + bbox.width);
    handle.curY = clamp(targetY, bbox.y, bbox.y + bbox.height);
    if (spatialMode) {
      // a source stays inside the circle, not the corners of its box
      const dx = handle.curX - circle.cx, dy = handle.curY - circle.cy;
      const d = Math.hypot(dx, dy);
      if (d > circle.radius) {
        handle.curX = circle.cx + dx * circle.radius / d;
        handle.curY = circle.cy + dy * circle.radius / d;
      }
    }
    handle.offsetX = handle.curX - handle.originalCenterX;
    handle.offsetY = handle.curY - handle.originalCenterY;
    
    if (handle.rotHandle) {
      // Both individual and shared (cloned) handles use the same update function
      updateRotationTransform(
        handle.el, 
        handle.rotHandle.el, 
        handle.curX, 
        handle.curY,
        handle.offsetX, 
        handle.offsetY,
        handle.touchptRadius,
        handle.rotHandle.originalCX,
        handle.rotHandle.originalCY,
        handle.originalCenterX,
        handle.originalCenterY,
        handle.curAngle
      );
    } else {
      // No rotation, just translate
      handle.el.setAttribute("transform", `translate(${handle.offsetX}, ${handle.offsetY})`);
    }
  }
  
  function applyRotation(handle, angleDeg) {
    // Apply rotation constraints based on hmode
    if (handle.hmode && handle.rotRange) {
      angleDeg = constrainRotation(angleDeg, handle.hmode, handle.rotRange);
    }
    
    handle.curAngle = angleDeg;
    
    if (handle.rotHandle) {
      // Both individual and shared (cloned) handles use the same update function
      updateRotationTransform(
        handle.el,
        handle.rotHandle.el,
        handle.curX,
        handle.curY,
        handle.offsetX,
        handle.offsetY,
        handle.touchptRadius,
        handle.rotHandle.originalCX,
        handle.rotHandle.originalCY,
        handle.originalCenterX,
        handle.originalCenterY,
        handle.curAngle
      );
    }
  }

  function findHandleByPointerId(pointerId) {
    return handles.find(h => h.pointerId === pointerId);
  }

  function findNearestFreeHandle(svgPt) {
    let nearest = null;
    let nearestDist = Infinity;
    for (const h of handles) {
      if (h.dragging) continue;
      const dx = h.curX - svgPt.x;
      const dy = h.curY - svgPt.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < nearestDist) {
        nearestDist = dist;
        nearest = h;
      }
    }
    return nearest;
  }

  // ---------------------------------------------
  // Mute overlay functions
  // ---------------------------------------------
  function showMuteOverlay(handle) {
    hideMuteOverlay(handle);
    
    const hbbox = handle.el.getBBox();
    const overlaySize = 24;
    const isMuted = isHandleMuted(uid, handle.id);
    
    const overlay = document.createElementNS("http://www.w3.org/2000/svg", "g");
    overlay.classList.add("controlxy-mute-overlay");
    overlay.style.cursor = "pointer";
    
    const overlayX = hbbox.x + hbbox.width / 2 + handle.offsetX + 18;
    const overlayY = hbbox.y + hbbox.height / 2 + handle.offsetY - 18;
    overlay.setAttribute("transform", `translate(${overlayX}, ${overlayY})`);
    
    const bg = document.createElementNS("http://www.w3.org/2000/svg", "circle");
    bg.setAttribute("cx", 0);
    bg.setAttribute("cy", 0);
    bg.setAttribute("r", overlaySize / 2);
    bg.setAttribute("fill", isMuted ? "#ff6b6b" : "#4ecdc4");
    bg.setAttribute("stroke", "#fff");
    bg.setAttribute("stroke-width", "2");
    bg.classList.add("controlxy-mute-overlay-bg");
    overlay.appendChild(bg);
    
    const icon = document.createElementNS("http://www.w3.org/2000/svg", "text");
    icon.setAttribute("x", 0);
    icon.setAttribute("y", 1);
    icon.setAttribute("text-anchor", "middle");
    icon.setAttribute("dominant-baseline", "middle");
    icon.setAttribute("font-size", "11");
    icon.setAttribute("font-weight", "bold");
    icon.setAttribute("fill", "#fff");
    icon.setAttribute("pointer-events", "none");
    icon.textContent = isMuted ? "M" : "A";
    overlay.appendChild(icon);
    
    overlay.addEventListener("click", (e) => {
      e.stopPropagation();
      e.preventDefault();
      const newMuted = toggleHandleMuted(uid, handle.id);
      bg.setAttribute("fill", newMuted ? "#ff6b6b" : "#4ecdc4");
      icon.textContent = newMuted ? "M" : "A";
      
      if (newMuted) {
        handle.el.classList.add("controlxy-handle--muted");
      } else {
        handle.el.classList.remove("controlxy-handle--muted");
      }
      
      clearTimeout(handle.muteOverlayTimer);
      handle.muteOverlayTimer = setTimeout(() => hideMuteOverlay(handle), 2000);
    });
    
    handle.el.parentNode.appendChild(overlay);
    handle.muteOverlay = overlay;
    
    overlay.style.opacity = "0";
    overlay.style.transition = "opacity 0.15s ease-out";
    requestAnimationFrame(() => {
      overlay.style.opacity = "1";
    });
    
    handle.muteOverlayTimer = setTimeout(() => hideMuteOverlay(handle), 2000);
  }
  
  function hideMuteOverlay(handle) {
    if (handle.muteOverlay) {
      handle.muteOverlay.style.opacity = "0";
      const overlay = handle.muteOverlay;
      setTimeout(() => overlay?.remove(), 150);
      handle.muteOverlay = null;
    }
    clearTimeout(handle.muteOverlayTimer);
  }

  // ---------------------------------------------
  // Pointer events
  // ---------------------------------------------
  function onPointerDown(e) {
    e.preventDefault();
    e.stopPropagation();

    const svgPt = svgPointFromEvent(e);
    let targetHandle = null;
    let isRotationHandle = false;
    
    // Check if we clicked on any rotation handle (individual or cloned shared)
    for (const h of handles) {
      if (h.rotHandle && h.rotHandle.el && (h.rotHandle.el === e.target || h.rotHandle.el.contains(e.target))) {
        targetHandle = h;
        isRotationHandle = true;
        break;
      }
    }
    
    // If not a rotation handle, check for touchpoint
    if (!targetHandle) {
      for (const h of handles) {
        if (h.el === e.target || h.el.contains(e.target)) {
          targetHandle = h;
          break;
        }
      }
    }

    // If still not found, find nearest free touchpoint
    if (!targetHandle) {
      targetHandle = findNearestFreeHandle(svgPt);
    }

    if (!targetHandle) return;
    
    // Check if rotation handle is being dragged
    if (isRotationHandle && targetHandle.rotHandle) {
      if (targetHandle.rotHandle.dragging || targetHandle.dragging) return;
      
      // Start rotation drag (works for both individual and cloned shared handles)
      targetHandle.rotHandle.dragging = true;
      targetHandle.rotHandle.pointerId = e.pointerId;
      targetHandle.rotHandle.el.setPointerCapture?.(e.pointerId);
      targetHandle.rotHandle.el.classList.add("controlxy-handle--active");
      
      // Calculate initial angle from touchpoint center
      const dx = svgPt.x - targetHandle.curX;
      const dy = svgPt.y - targetHandle.curY;
      let angle = Math.atan2(dy, dx) * 180 / Math.PI;
      // Convert to our coordinate system (0 = 7 o'clock = 120°)
      angle = ((angle - 120) + 360) % 360;
      
      applyRotation(targetHandle, angle);
      emit(targetHandle);
      return;
    }

    // Regular touchpoint drag (X/Y movement)
    if (targetHandle.dragging || (targetHandle.rotHandle && targetHandle.rotHandle.dragging)) return;

    // Show mute overlay after brief delay (not during drag start)
    clearTimeout(targetHandle.showOverlayTimer);
    targetHandle.showOverlayTimer = setTimeout(() => {
      if (!targetHandle.dragging) {
        showMuteOverlay(targetHandle);
      }
    }, 250);

    targetHandle.dragging = true;
    targetHandle.pointerId = e.pointerId;
    targetHandle.el.setPointerCapture?.(e.pointerId);
    targetHandle.el.classList.add("controlxy-handle--active");
    
    if (targetHandle.label) {
      targetHandle.label.classList.add("dragging");
    }
    
    if (lastTouchedHandle && lastTouchedHandle !== targetHandle && lastTouchedHandle.label) {
      lastTouchedHandle.label.classList.remove("last-touched");
    }
    lastTouchedHandle = targetHandle;
    
    applyPosition(targetHandle, svgPt.x, svgPt.y);
    emit(targetHandle);
  }
  
  function onPointerMove(e) {
    const svgPt = svgPointFromEvent(e);
    
    // Check if any rotation handle is being dragged
    for (const handle of handles) {
      if (handle.rotHandle && handle.rotHandle.el && handle.rotHandle.dragging && handle.rotHandle.pointerId === e.pointerId) {
        // Calculate angle from touchpoint center to pointer
        const dx = svgPt.x - handle.curX;
        const dy = svgPt.y - handle.curY;
        let angle = Math.atan2(dy, dx) * 180 / Math.PI;
        // Convert to our coordinate system (0 = 7 o'clock = 120°)
        angle = ((angle - 120) + 360) % 360;
        
        applyRotation(handle, angle);
        emit(handle);
        return;
      }
    }
    
    // Regular touchpoint dragging
    const handle = findHandleByPointerId(e.pointerId);
    if (!handle || !handle.dragging) return;
    
    // Cancel mute overlay show if dragging
    clearTimeout(handle.showOverlayTimer);

    applyPosition(handle, svgPt.x, svgPt.y);
    emit(handle);
  }

  function onPointerUp(e) {
    // Check if any rotation handle is being released
    for (const handle of handles) {
      if (handle.rotHandle && handle.rotHandle.el && handle.rotHandle.pointerId === e.pointerId) {
        handle.rotHandle.dragging = false;
        handle.rotHandle.pointerId = null;
        handle.rotHandle.el.releasePointerCapture?.(e.pointerId);
        handle.rotHandle.el.classList.remove("controlxy-handle--active");
        return;
      }
    }
    
    // Regular touchpoint release
    const handle = findHandleByPointerId(e.pointerId);
    if (!handle) return;

    handle.dragging = false;
    handle.pointerId = null;
    handle.el.releasePointerCapture?.(e.pointerId);
    handle.el.classList.remove("controlxy-handle--active");
    
    if (handle.label) {
      handle.label.classList.remove("dragging");
      handle.label.classList.add("last-touched");
    }
  }

  function onPointerCancel(e) {
    onPointerUp(e);
  }

  // Register events (all of them on `signal`: cleanup() aborts them at once)
  for (const handle of handles) {
    handle.el.addEventListener("pointerdown", onPointerDown, { signal });
    handle.el.addEventListener("pointerenter", () => {
      if (handle.label && !handle.dragging) {
        handle.label.classList.add("hovered");
      }
    }, { signal });
    handle.el.addEventListener("pointerleave", () => {
      if (handle.label) {
        handle.label.classList.remove("hovered");
      }
    }, { signal });

    // Register events for rotation handle (individual or cloned shared)
    if (handle.rotHandle && handle.rotHandle.el) {
      handle.rotHandle.el.addEventListener("pointerdown", onPointerDown, { signal });
    }
  }

  // Prevent score dragging
  const preventScoreDrag = (e) => {
    e.stopPropagation();
    if (e.type === 'pointerdown' || e.type === 'mousedown' || e.type === 'touchstart') {
      e.preventDefault();
    }
  };

  boundsEl.addEventListener("pointerdown", preventScoreDrag, { capture: true, signal });
  boundsEl.addEventListener("mousedown", preventScoreDrag, { capture: true, signal });
  boundsEl.addEventListener("touchstart", preventScoreDrag, { passive: false, capture: true, signal });

  // Global move/up handlers
  window.addEventListener("pointermove", onPointerMove, { signal });
  window.addEventListener("pointerup", onPointerUp, { signal });
  window.addEventListener("pointercancel", onPointerCancel, { signal });

  // Initial emit
  for (const handle of handles) {
    emit(handle);
  }

  // ---------------------------------------------
  // External position updates (presets, tweening)
  // Respects mute state!
  // ---------------------------------------------
  function setPosition(handleId, normX, normY, normP) {
    // Check if handle is muted
    if (isHandleMuted(uid, handleId)) {
      return;
    }
    
    const handle = handles.find(h => h.id === handleId);
    if (!handle) return;
    
    const targetX = bbox.x + normX * bbox.width;
    const targetY = bbox.y + (1 - normY) * bbox.height;
    
    applyPosition(handle, targetX, targetY);
    
    // Apply rotation if provided and handle has rotation capability
    if (normP !== undefined && handle.rotHandle) {
      // Convert normalized rotation to angle based on hmode
      const angleDeg = denormalizeRotation(normP, handle.hmode, handle.rotRange);
      applyRotation(handle, angleDeg);
    }
    
    emit(handle);
  }

  function captureState() {
    const state = {};
    for (const handle of handles) {
      const normX = clamp((handle.curX - bbox.x) / bbox.width, 0, 1);
      const normY = clamp(1 - (handle.curY - bbox.y) / bbox.height, 0, 1);
      
      const handleState = { x: normX, y: normY };
      
      if (handle.rotHandle) {
        // Normalize rotation based on hmode using shared math
        const normP = normalizeRotation(handle.curAngle, handle.hmode, handle.rotRange);
        handleState.p = normP;
      }
      
      state[handle.id] = handleState;
    }
    return state;
  }

  function applyPositions(positions, options = {}) {
    for (const [handleId, pos] of Object.entries(positions)) {
      // Check mute state
      if (isHandleMuted(uid, handleId)) {
        continue;
      }
      setPosition(handleId, pos.x, pos.y, pos.p);
    }
  }

  // Listen for remote position updates from other clients
  // In composition/ensemble mode, ignore manual moves (only preset recalls sync)
  window.addEventListener("oscilla:pos-sync", (e) => {
    const presetScope = window.presetScope || 'user';
    if (presetScope === 'composition') return; // Ignore manual moves in ensemble mode

    const d = e.detail;
    if (d.kind !== "xy" || d.uid !== uid) return;
    _isRemoteUpdate = true;
    setPosition(d.handleId, d.x, d.y);
    _isRemoteUpdate = false;
  }, { signal });

  // A late joiner: the handles others had moved, from the server's snapshot
  // (socket.js), which arrives on connect, before the pads exist
  if ((window.presetScope || 'user') !== 'composition') {
    for (const p of Object.values(window._posSyncSnapshot || {})) {
      if (p?.kind !== "xy" || p.uid !== uid) continue;
      _isRemoteUpdate = true;
      setPosition(p.handleId, p.x, p.y);
      _isRemoteUpdate = false;
    }
  }

  // Cleanup: every listener (window, handles, bounds, launcher), the launcher
  // and its toggle circle, labels, mute overlays and shared-handle clones
  function cleanup() {
    ac.abort();

    for (const handle of handles) {
      handle.label?.remove();
      hideMuteOverlay(handle);
    }

    launcherElement?._toggleCircle?.remove();
    launcherElement?.remove();

    const sharedHandle = window._controlXYSharedHandle?.[uid];
    if (sharedHandle) {
      for (const c of sharedHandle.clones) c.handle.el.remove();
      sharedHandle.templateEl.style.display = '';
      delete window._controlXYSharedHandle[uid];
    }

    if (window._controlXYPads?.[uid]?.cleanup === cleanup) delete window._controlXYPads[uid];
  }

  // Register pad
  window._controlXYPads = window._controlXYPads || {};
  window._controlXYPads[uid] = {
    uid,
    handles: handles.map(h => h.id),
    setPosition,
    captureState,
    applyPositions,
    getBounds: () => bbox,
    cleanup
  };

  return { cleanup, uid, handles, setPosition, captureState, applyPositions };
}

/** A project switch: every pad taken down and every mute forgotten. */
export function destroyAllControlXYPads() {
  for (const pad of Object.values(window._controlXYPads || {})) pad.cleanup?.();
  mutedHandles.clear();
}
onReset("project", destroyAllControlXYPads);

// ============================================================================
// LAUNCHER with Sequence Transport Controls
// ============================================================================

function createLauncher(uid, bbox, slots, totalBanks, parent, signal) {
  const buttonHeight = 44;
  const buttonSpacing = 5;
  const bankBarHeight = 34;
  const padding = 8;
  const totalHeight = buttonHeight + buttonSpacing + bankBarHeight + padding;
  
  const launcherY = bbox.y + bbox.height + 8;
  
  const fo = document.createElementNS("http://www.w3.org/2000/svg", "foreignObject");
  fo.setAttribute("x", bbox.x);
  fo.setAttribute("y", launcherY);
  fo.setAttribute("width", bbox.width);
  fo.setAttribute("height", totalHeight);
  fo.classList.add("controlxy-launcher-container");
  fo.style.overflow = "visible";
  
  const container = document.createElement("div");
  container.className = "controlxy-launcher";
  container.dataset.uid = uid;
  container.style.width = "100%";
  container.style.height = "100%";
  container.style.overflow = "visible";
  container.style.boxSizing = "border-box";
  
  // Button row
  const buttonRow = document.createElement("div");
  buttonRow.className = "controlxy-launcher-buttons";
  
  for (let i = 0; i < slots; i++) {
    const btn = document.createElement("button");
    btn.className = "controlxy-launcher-slot";
    btn.dataset.slot = i;
    btn.innerHTML = `<span class="slot-number">${i + 1}</span><span class="slot-label">Empty</span>`;
    btn.title = "Left-click: Recall | Long-press: Store current";
    buttonRow.appendChild(btn);
  }
  
  container.appendChild(buttonRow);
  
  // Bank bar with sequence transport
  const bankBar = document.createElement("div");
  bankBar.className = "controlxy-launcher-bank-bar";
  bankBar.innerHTML = `
    <button class="controlxy-launcher-seq-play" data-action="seq-play" title="Play Sequence">[>]</button>
    <button class="controlxy-launcher-seq-stop" data-action="seq-stop" title="Stop Sequence">[X]</button>
    <span class="controlxy-launcher-seq-status"></span>
    <span class="controlxy-launcher-spacer"></span>
    <button class="controlxy-launcher-bank-btn" data-action="prev" title="Previous Bank">[<-]</button>
    <span class="controlxy-launcher-bank-label">
      <span class="bank-name">Bank 1</span>
      <span class="bank-count">(1/${totalBanks})</span>
    </span>
    <button class="controlxy-launcher-bank-btn" data-action="next" title="Next Bank">[->]</button>
    <button class="controlxy-launcher-mode-btn" data-mode="preset" title="Toggle Preset/Sequence Mode">P</button>
    <button class="controlxy-launcher-tween-btn active" data-tween="true" title="Toggle Tween/Jump">~</button>
    <button class="controlxy-launcher-settings-btn" title="Open Preset Manager">[*]</button>
  `;
  container.appendChild(bankBar);
  
  fo.appendChild(container);
  parent.appendChild(fo);
  
  // -- Toggle circle: dot at bottom-left of the bounds bbox.
  // Clicking it shows/hides the launcher foreignObject. Placed as a
  // sibling SVG element (not inside the foreignObject) so it remains
  // visible when the launcher is hidden.
  const circleR = 7.5;
  const circleX = bbox.x - 20;
  const circleY = bbox.y + bbox.height + 20;

  const circle = document.createElementNS("http://www.w3.org/2000/svg", "circle");
  circle.setAttribute("cx", circleX);
  circle.setAttribute("cy", circleY);
  circle.setAttribute("r", circleR);
  circle.setAttribute("fill", "rgba(41, 128, 185, 0.15)");
  circle.setAttribute("stroke", "#2980b9");
  circle.setAttribute("stroke-width", "2");
  circle.setAttribute("cursor", "pointer");
  circle.classList.add("controlxy-hideshow-circle");
  circle.style.pointerEvents = "all";

  // Toggle function for showing/hiding the launcher
  let lastToggleTime = 0;
  const toggleLauncher = () => {
    // Debounce to prevent double-toggle from both pointer and click events
    const now = Date.now();
    if (now - lastToggleTime < 100) return;
    lastToggleTime = now;

    const visible = fo.style.display !== "none";
    fo.style.display = visible ? "none" : "";
  };

  // Click handler for synthetic click events dispatched by overlay
  circle.addEventListener("click", (e) => {
    e.stopPropagation();
    e.preventDefault();
    toggleLauncher();
  });

  // Pointer handlers for direct interaction
  let pointerDownTime = 0;
  let pointerDownPos = { x: 0, y: 0 };

  circle.addEventListener("pointerdown", (e) => {
    e.stopPropagation();
    pointerDownTime = Date.now();
    pointerDownPos = { x: e.clientX, y: e.clientY };
  });

  circle.addEventListener("pointerup", (e) => {
    e.stopPropagation();
    const elapsed = Date.now() - pointerDownTime;
    const dx = Math.abs(e.clientX - pointerDownPos.x);
    const dy = Math.abs(e.clientY - pointerDownPos.y);
    // Toggle on tap (short press with minimal movement)
    if (elapsed < 500 && dx < 15 && dy < 15) {
      e.preventDefault();
      toggleLauncher();
    }
  });

  parent.appendChild(circle);
  fo._toggleCircle = circle;   // removed with the launcher
  
  initializeLauncher(uid, container, slots, totalBanks, signal);
  
  return fo;
}

function initializeLauncher(uid, container, slots, totalBanks, signal) {
  if (!shared.state.initialized) {
    const project = shared.getProjectName();
    if (project && project !== "unknown_project") {
      shared.init(project);
    }
  }
  
  const launcherData = shared.getOrCreateLauncher(uid, {
    currentBank: 0,
    mode: 'preset',
    tween: true,
    visible: true,
    banks: Array(totalBanks).fill(null).map((_, i) => ({
      name: `Bank ${i + 1}`,
      slots: Array(slots).fill(null)
    }))
  });
  
  const state = launcherData;
  
  while (state.banks.length < totalBanks) {
    state.banks.push({
      name: `Bank ${state.banks.length + 1}`,
      slots: Array(slots).fill(null)
    });
  }
  
  state.banks.forEach(bank => {
    while ((bank.slots?.length || 0) < slots) {
      bank.slots = bank.slots || [];
      bank.slots.push(null);
    }
  });
  
  container.dataset.uid = uid;
  
  function getSequenceList() {
    return shared.listSequences();
  }
  
  function saveLauncherState() {
    shared.saveLauncher(uid, state);
  }
  
  // Sequence status element
  const seqStatus = container.querySelector('.controlxy-launcher-seq-status');
  const seqPlayBtn = container.querySelector('.controlxy-launcher-seq-play');
  const seqStopBtn = container.querySelector('.controlxy-launcher-seq-stop');
  
  // Update sequence status display
  function updateSeqStatus() {
    const active = window.controlXYPresets?.getActiveSequence?.();
    if (active && active.running) {
      seqStatus.textContent = `[>] ${active.name}`;
      seqStatus.classList.add('active');
      seqPlayBtn.classList.add('playing');
      seqStopBtn.classList.add('active');
    } else {
      seqStatus.textContent = '';
      seqStatus.classList.remove('active');
      seqPlayBtn.classList.remove('playing');
      seqStopBtn.classList.remove('active');
    }
  }
  
  // Listen for sequence events
  window.addEventListener("controlxy:sequenceStarted", updateSeqStatus, { signal });
  window.addEventListener("controlxy:sequenceStopped", updateSeqStatus, { signal });
  window.addEventListener("controlxy:sequenceStep", updateSeqStatus, { signal });
  
  // Initial status update
  setTimeout(updateSeqStatus, 100);
  
  function renderBank() {
    const bank = state.banks[state.currentBank];
    const bankLabel = container.querySelector('.bank-name');
    const bankCount = container.querySelector('.bank-count');
    const buttons = container.querySelectorAll('.controlxy-launcher-slot');
    const modeBtn = container.querySelector('.controlxy-launcher-mode-btn');
    const tweenBtn = container.querySelector('.controlxy-launcher-tween-btn');
    
    if (state.mode === 'sequence') {
      modeBtn.textContent = 'S';
      modeBtn.classList.add('sequence-mode');
      modeBtn.title = 'Sequence Mode (click for Preset mode)';
    } else {
      modeBtn.textContent = 'P';
      modeBtn.classList.remove('sequence-mode');
      modeBtn.title = 'Preset Mode (click for Sequence mode)';
    }
    
    if (state.tween) {
      tweenBtn.classList.add('active');
      tweenBtn.title = 'Tween ON (click to Jump)';
    } else {
      tweenBtn.classList.remove('active');
      tweenBtn.title = 'Jump (click for Tween)';
    }
    
    bankLabel.textContent = bank.name;
    bankCount.textContent = `(${state.currentBank + 1}/${totalBanks})`;
    
    if (state.mode === 'sequence') {
      const sequences = getSequenceList();
      const startIndex = state.currentBank * slots;
      
      buttons.forEach((btn, i) => {
        const slotLabel = btn.querySelector('.slot-label');
        const seqIndex = startIndex + i;
        
        if (seqIndex < sequences.length) {
          const seqName = sequences[seqIndex];
          btn.classList.add('assigned', 'type-sequence');
          btn.classList.remove('empty');
          btn.dataset.type = 'sequence';
          btn.dataset.name = seqName;
          slotLabel.textContent = seqName;
        } else {
          btn.classList.remove('assigned', 'type-sequence');
          btn.classList.add('empty');
          slotLabel.textContent = 'Empty';
          delete btn.dataset.type;
          delete btn.dataset.name;
        }
      });
    } else {
      buttons.forEach((btn, i) => {
        const slot = bank.slots[i];
        const slotLabel = btn.querySelector('.slot-label');
        
        if (slot) {
          btn.classList.add('assigned');
          btn.classList.remove('empty');
          btn.dataset.type = slot.type;
          btn.dataset.name = slot.name;
          slotLabel.textContent = slot.name;
          
          if (slot.type === 'sequence') {
            btn.classList.add('type-sequence');
          } else {
            btn.classList.remove('type-sequence');
          }
        } else {
          btn.classList.remove('assigned', 'type-sequence');
          btn.classList.add('empty');
          slotLabel.textContent = 'Empty';
          delete btn.dataset.type;
          delete btn.dataset.name;
        }
      });
    }
    
    updateSeqStatus();
  }
  
  // Bank navigation
  container.querySelector('[data-action="prev"]').addEventListener('click', (e) => {
    e.stopPropagation();
    state.currentBank = (state.currentBank - 1 + totalBanks) % totalBanks;
    renderBank();
    saveLauncherState();
  });
  
  container.querySelector('[data-action="next"]').addEventListener('click', (e) => {
    e.stopPropagation();
    state.currentBank = (state.currentBank + 1) % totalBanks;
    renderBank();
    saveLauncherState();
  });
  
  // Sequence transport controls
  seqPlayBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    const active = window.controlXYPresets?.getActiveSequence?.();
    if (active && active.running) {
      // Already playing - could toggle pause here if supported
    } else {
      // Try to play last used sequence or first available
      const sequences = getSequenceList();
      if (sequences.length > 0) {
        const seqName = sequences[0];
        window.controlXYPresets?.playSequence(seqName, {
          dur: state.tween ? 1 : 0
        });
      }
    }
    updateSeqStatus();
  });
  
  seqStopBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    window.controlXYPresets?.stopSequence?.();
    updateSeqStatus();
  });
  
  // Mode toggle
  container.querySelector('.controlxy-launcher-mode-btn').addEventListener('click', (e) => {
    e.stopPropagation();
    state.mode = state.mode === 'preset' ? 'sequence' : 'preset';
    renderBank();
    saveLauncherState();
  });
  
  // Tween toggle
  container.querySelector('.controlxy-launcher-tween-btn').addEventListener('click', (e) => {
    e.stopPropagation();
    state.tween = !state.tween;
    renderBank();
    saveLauncherState();
  });
  
  // Settings
  container.querySelector('.controlxy-launcher-settings-btn').addEventListener('click', (e) => {
    e.stopPropagation();
    window.controlXYPresetUI?.toggle();
  });
  
  // Slot buttons
  container.querySelectorAll('.controlxy-launcher-slot').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      
      const name = btn.dataset.name;
      const type = btn.dataset.type;
      
      if (!name) return;
      
      container.querySelectorAll('.controlxy-launcher-slot').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      
      const dur = state.tween ? 1 : 0;
      
      if (type === 'preset') {
        window.controlXYPresets?.recall(name, { dur, ease: 'easeInOutSine' });
      } else if (type === 'sequence') {
        window.controlXYPresets?.playSequence(name, { dur });
      }
      
      setTimeout(() => btn.classList.remove('active'), 300);
      updateSeqStatus();
    });
    
    btn.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      const slotIndex = parseInt(btn.dataset.slot);
      const slotName = prompt('Save current state as:', `slot_${state.currentBank + 1}_${slotIndex + 1}`);
      
      if (!slotName) return;
      
      window.controlXYPresets?.save(slotName);
      
      state.banks[state.currentBank].slots[slotIndex] = {
        type: 'preset',
        name: slotName
      };
      
      renderBank();
      saveLauncherState();
    });
    
    let pressTimer;
    btn.addEventListener('pointerdown', (e) => {
      if (e.button !== 0) return;
      
      pressTimer = setTimeout(() => {
        const contextEvent = new MouseEvent('contextmenu', {
          bubbles: true,
          cancelable: true,
          view: window
        });
        btn.dispatchEvent(contextEvent);
      }, 500);
    });
    
    btn.addEventListener('pointerup', () => clearTimeout(pressTimer));
    btn.addEventListener('pointercancel', () => clearTimeout(pressTimer));
  });
  
  renderBank();
  
  // Refresh handlers
  const refreshHandler = (event) => {
    const { uid: targetUid } = event.detail || {};
    if (!targetUid || targetUid === uid) {
      const launcher = shared.getLauncher(uid);
      const updatedState = launcher?.data;
      if (updatedState) {
        state.currentBank = updatedState.currentBank ?? state.currentBank;
        state.mode = updatedState.mode ?? state.mode;
        state.tween = updatedState.tween ?? state.tween;
        state.visible = updatedState.visible ?? state.visible;
        if (updatedState.banks) {
          state.banks = updatedState.banks;
        }
      }
      renderBank();
    }
  };
  
  window.addEventListener("controlxy:launcherRefresh", refreshHandler, { signal });
  
  const loadedHandler = (event) => {
    const launcher = shared.getLauncher(uid);
    const loadedState = launcher?.data;
    if (loadedState) {
      state.currentBank = loadedState.currentBank ?? state.currentBank;
      state.mode = loadedState.mode ?? state.mode;
      state.tween = loadedState.tween ?? state.tween;
      state.visible = loadedState.visible ?? state.visible;
      if (loadedState.banks && loadedState.banks.length > 0) {
        state.banks = loadedState.banks;
        state.banks.forEach(bank => {
          while ((bank.slots?.length || 0) < slots) {
            bank.slots = bank.slots || [];
            bank.slots.push(null);
          }
        });
      }
      renderBank();
    }
  };
  
  window.addEventListener("controlxy:loaded", loadedHandler, { signal });
}

export default handleControlXYCue;
